/**
 * `/lieferanten` — wer schickt uns Rechnungen, und wohin wird gezahlt.
 *
 * **Die ganze Seite ist `admin`** (§15.1 S3), und das ist keine Vorsicht,
 * sondern die Kernaussage: Wer eine IBAN bestätigen kann, bestimmt, wohin das
 * Geld geht. Diese Liste ist der einzige Ort, an dem eine IBAN als „bekannt"
 * gilt — die Zu-zahlen-Liste zeigt nie die IBAN vom Beleg, sondern nur eine
 * von hier.
 *
 * Deshalb steht bei jeder IBAN, **wer** sie **wann** bestätigt hat. Eine
 * unbestätigte IBAN ist kein Fehler; sie ist eine, die zuerst gesehen wurde
 * und auf ihren Rückruf wartet.
 *
 * Die Liste entsteht im Betrieb über „Freigeben" — der CSV-Import aus dem
 * Bauplan ist gestrichen (§15.3). Der JSON-Import unten ist die Abkürzung für
 * einen Startbestand, das Muster vom Förderradar.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, CircleAlert, Pencil, Plus, Search, Upload } from 'lucide-react';

import { bh, fehlerText } from '@/lib/api';
import { datum, iban as ibanFormat } from '@/lib/format';
import { istAdmin } from '@/lib/rechte';
import { useAuth } from '@/features/auth/AuthContext';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { Dialog } from '@/components/ui/Dialog';
import { Eingabe, Feld, Seitenkopf, Textfeld } from '@/components/ui/Feld';
import { useToast } from '@/components/ui/Toast';
import { Band, Fehler, Laden, Leer } from '@/components/ui/Zustaende';
import type {
  IbanBestaetigung,
  SeedImport,
  SupplierCreate,
  SupplierResponse,
} from '@/types';

export function LieferantenPage() {
  const { user } = useAuth();
  const cache = useQueryClient();
  const toast = useToast();
  const admin = istAdmin(user);

  const [suche, setSuche] = useState('');
  const [bearbeiten, setBearbeiten] = useState<SupplierResponse | null>(null);
  const [neu, setNeu] = useState(false);
  const [ibanFuer, setIbanFuer] = useState<SupplierResponse | null>(null);
  const [importOffen, setImportOffen] = useState(false);

  const liste = useQuery({
    queryKey: ['lieferanten', suche],
    queryFn: () => bh.lieferanten(suche || undefined),
    enabled: admin,
  });

  const frisch = () => void cache.invalidateQueries({ queryKey: ['lieferanten'] });

  const anlegen = useMutation({
    mutationFn: (daten: SupplierCreate) => bh.lieferantAnlegen(daten),
    onSuccess: () => {
      frisch();
      setNeu(false);
      toast.erfolg('Lieferant angelegt.');
    },
    onError: (fehler) => toast.fehler(fehlerText(fehler)),
  });

  const aendern = useMutation({
    mutationFn: (arg: { id: string; daten: SupplierCreate }) =>
      bh.lieferantAendern(arg.id, arg.daten),
    onSuccess: () => {
      frisch();
      setBearbeiten(null);
      toast.erfolg('Gespeichert.');
    },
    onError: (fehler) => toast.fehler(fehlerText(fehler)),
  });

  const bestaetigen = useMutation({
    mutationFn: (arg: { id: string; daten: IbanBestaetigung }) =>
      bh.ibanBestaetigen(arg.id, arg.daten),
    onSuccess: () => {
      frisch();
      setIbanFuer(null);
      toast.erfolg('IBAN bestätigt. Alle Admins bekommen eine Benachrichtigung.');
    },
    onError: (fehler) => toast.fehler(fehlerText(fehler)),
  });

  const einlesen = useMutation({
    mutationFn: (daten: SeedImport) => bh.lieferantenSeedImport(daten),
    onSuccess: (antwort) => {
      frisch();
      setImportOffen(false);
      toast.erfolg(`Eingelesen. Die Liste hat jetzt ${antwort.total} Einträge.`);
    },
    onError: (fehler) => toast.fehler(fehlerText(fehler)),
  });

  if (!admin) {
    return (
      <div>
        <Seitenkopf titel="Lieferanten" />
        <Band farbe="warnung">
          Diese Seite ist Admins vorbehalten. Wer eine IBAN bestätigt, bestimmt, wohin gezahlt
          wird — deshalb ist die Liste nicht für alle offen.
        </Band>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <Seitenkopf
        titel="Lieferanten"
        beschreibung="Wer uns Rechnungen schickt — mit den IBANs, auf die gezahlt werden darf."
      >
        <Button variant="outline" size="sm" onClick={() => setImportOffen(true)}>
          <Upload size={14} className="mr-1.5" />
          Startbestand einlesen
        </Button>
        <Button size="sm" onClick={() => setNeu(true)}>
          <Plus size={14} className="mr-1.5" />
          Lieferant
        </Button>
      </Seitenkopf>

      <div className="mb-4 flex items-center gap-2">
        <div className="relative flex-1">
          <Search
            size={15}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <Eingabe
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder="Name, Alias oder Domain"
            className="pl-9"
          />
        </div>
      </div>

      {liste.isLoading ? (
        <Laden text="Lieferanten werden geladen …" />
      ) : liste.isError || !liste.data ? (
        <Fehler text={fehlerText(liste.error)} onErneut={() => void liste.refetch()} />
      ) : liste.data.items.length === 0 ? (
        <Leer
          titel={suche ? 'Kein Treffer.' : 'Noch keine Lieferanten.'}
          hinweis={
            suche
              ? 'Andere Schreibweise versuchen — gesucht wird in Name, Aliassen und Domains.'
              : 'Die Liste entsteht im Betrieb: Wer einen Beleg freigibt, legt den Lieferanten an. Ein Startbestand lässt sich oben einlesen.'
          }
        />
      ) : (
        <ul className="space-y-3">
          {liste.data.items.map((lieferant) => (
            <LieferantKarte
              key={lieferant.id}
              lieferant={lieferant}
              onBearbeiten={() => setBearbeiten(lieferant)}
              onIban={() => setIbanFuer(lieferant)}
            />
          ))}
        </ul>
      )}

      <LieferantDialog
        offen={neu || bearbeiten !== null}
        lieferant={bearbeiten}
        laeuft={anlegen.isPending || aendern.isPending}
        onAbbrechen={() => {
          setNeu(false);
          setBearbeiten(null);
        }}
        onSpeichern={(daten) =>
          bearbeiten
            ? aendern.mutate({ id: bearbeiten.id, daten })
            : anlegen.mutate(daten)
        }
      />

      <IbanDialog
        lieferant={ibanFuer}
        laeuft={bestaetigen.isPending}
        onAbbrechen={() => setIbanFuer(null)}
        onBestaetigen={(daten) =>
          ibanFuer && bestaetigen.mutate({ id: ibanFuer.id, daten })
        }
      />

      <SeedDialog
        offen={importOffen}
        laeuft={einlesen.isPending}
        onAbbrechen={() => setImportOffen(false)}
        onEinlesen={(daten) => einlesen.mutate(daten)}
      />
    </div>
  );
}

function LieferantKarte({
  lieferant,
  onBearbeiten,
  onIban,
}: {
  lieferant: SupplierResponse;
  onBearbeiten: () => void;
  onIban: () => void;
}) {
  const unbestaetigt = lieferant.ibans.filter((i) => !i.confirmed_at).length;

  return (
    <li className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-900">{lieferant.name}</h3>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            {lieferant.mail_domains.length > 0 ? (
              <span>{lieferant.mail_domains.join(' · ')}</span>
            ) : (
              <span className="text-slate-400">keine Domain hinterlegt</span>
            )}
            <span>Rhythmus: {lieferant.expected_interval ?? 'unbekannt'}</span>
            {lieferant.customer_number && <span>Kundennr. {lieferant.customer_number}</span>}
            <span>
              {lieferant.belege_gesamt} {lieferant.belege_gesamt === 1 ? 'Beleg' : 'Belege'}
            </span>
          </p>
          {lieferant.aliases.length > 0 && (
            <p className="mt-1 text-xs text-slate-400">
              auch: {lieferant.aliases.join(', ')}
            </p>
          )}
          {lieferant.notes && (
            <p className="mt-1.5 text-xs text-slate-600">{lieferant.notes}</p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Button variant="outline" size="sm" onClick={onIban}>
            <BadgeCheck size={14} className="mr-1.5" />
            IBAN bestätigen
          </Button>
          <Button variant="ghost" size="sm" onClick={onBearbeiten}>
            <Pencil size={14} className="mr-1.5" />
            Bearbeiten
          </Button>
        </div>
      </div>

      <div className="mt-3 border-t border-slate-100 pt-3">
        {lieferant.ibans.length === 0 ? (
          <p className="text-xs text-slate-400">
            Keine IBAN hinterlegt — an diesen Lieferanten kann die Zu-zahlen-Liste nichts
            vorschlagen.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {lieferant.ibans.map((eintrag) => (
              <li key={eintrag.id} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-mono text-slate-800">{ibanFormat(eintrag.iban)}</span>
                {eintrag.confirmed_at ? (
                  <Chip
                    klassen="border-success-200 bg-success-50 text-success-700"
                    titel="Nur bestätigte IBANs erscheinen auf der Zu-zahlen-Liste."
                  >
                    <BadgeCheck size={12} />
                    bestätigt von {eintrag.confirmed_by_name ?? 'unbekannt'} am{' '}
                    {datum(eintrag.confirmed_at)}
                  </Chip>
                ) : (
                  <Chip
                    klassen="border-warning-200 bg-warning-50 text-warning-700"
                    titel="Zuerst gesehen, noch nicht per Rückruf bestätigt."
                  >
                    <CircleAlert size={12} />
                    unbestätigt
                  </Chip>
                )}
              </li>
            ))}
          </ul>
        )}
        {unbestaetigt > 0 && (
          <p className="mt-2 text-xs text-warning-700">
            {unbestaetigt} {unbestaetigt === 1 ? 'IBAN wartet' : 'IBANs warten'} auf einen
            Rückruf. Bis dahin schlägt die Zu-zahlen-Liste sie nicht vor.
          </p>
        )}
      </div>
    </li>
  );
}

/** Anlegen und Bearbeiten in einem Dialog — es sind dieselben Felder. */
function LieferantDialog({
  offen,
  lieferant,
  laeuft,
  onAbbrechen,
  onSpeichern,
}: {
  offen: boolean;
  lieferant: SupplierResponse | null;
  laeuft: boolean;
  onAbbrechen: () => void;
  onSpeichern: (daten: SupplierCreate) => void;
}) {
  const [name, setName] = useState('');
  const [aliases, setAliases] = useState('');
  const [domains, setDomains] = useState('');
  const [intervall, setIntervall] = useState('');
  const [kundennummer, setKundennummer] = useState('');
  const [notiz, setNotiz] = useState('');
  const [geladen, setGeladen] = useState<string | null>(null);

  // Beim Öffnen einmal aus dem Lieferanten füllen. Ein `useEffect` wäre hier
  // ein Umweg: der Dialog wird nicht neu montiert, aber der Schlüssel unten
  // sagt eindeutig, welcher Datensatz gerade im Formular steht.
  const schluessel = offen ? (lieferant?.id ?? 'neu') : null;
  if (schluessel !== geladen) {
    setGeladen(schluessel);
    setName(lieferant?.name ?? '');
    setAliases((lieferant?.aliases ?? []).join(', '));
    setDomains((lieferant?.mail_domains ?? []).join(', '));
    setIntervall(lieferant?.expected_interval ?? '');
    setKundennummer(lieferant?.customer_number ?? '');
    setNotiz(lieferant?.notes ?? '');
  }

  const liste = (wert: string) =>
    wert
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

  return (
    <Dialog
      offen={offen}
      onSchliessen={onAbbrechen}
      titel={lieferant ? 'Lieferant bearbeiten' : 'Lieferant anlegen'}
      hinweis="Domains und Rhythmus füttern die Echtheitsprüfung: Ein Absender von einer fremden Domain und eine Rechnung außer der Reihe fallen dadurch auf."
      abbrechenBeiHintergrundklick={false}
      fussleiste={
        <>
          <Button variant="outline" onClick={onAbbrechen} disabled={laeuft}>
            Abbrechen
          </Button>
          <Button
            onClick={() =>
              onSpeichern({
                name: name.trim(),
                aliases: liste(aliases),
                mail_domains: liste(domains),
                expected_interval: intervall.trim() || null,
                customer_number: kundennummer.trim() || null,
                notes: notiz.trim() || null,
              })
            }
            disabled={name.trim().length === 0}
            loading={laeuft}
          >
            Speichern
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Feld label="Name" pflicht>
          <Eingabe
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Telekom Deutschland GmbH"
          />
        </Feld>
        <div className="grid gap-3 sm:grid-cols-2">
          <Feld label="Aliasse" hinweis="Mit Komma trennen.">
            <Eingabe
              value={aliases}
              onChange={(e) => setAliases(e.target.value)}
              placeholder="Telekom, Deutsche Telekom AG"
            />
          </Feld>
          <Feld label="Mail-Domains" hinweis="Mit Komma trennen.">
            <Eingabe
              value={domains}
              onChange={(e) => setDomains(e.target.value)}
              placeholder="telekom.de, telekom-rechnung.de"
            />
          </Feld>
          <Feld label="Rhythmus" hinweis="Wie oft kommt eine Rechnung?">
            <Eingabe
              value={intervall}
              onChange={(e) => setIntervall(e.target.value)}
              placeholder="monatlich"
            />
          </Feld>
          <Feld label="Kundennummer">
            <Eingabe
              value={kundennummer}
              onChange={(e) => setKundennummer(e.target.value)}
              placeholder="0491234567"
            />
          </Feld>
        </div>
        <Feld label="Notiz">
          <Textfeld
            rows={2}
            value={notiz}
            onChange={(e) => setNotiz(e.target.value)}
            placeholder="Rückrufnummer aus dem Vertrag, nicht aus der Mail."
          />
        </Feld>
      </div>
    </Dialog>
  );
}

/**
 * Eine IBAN bestätigen — mit Rückrufnummer und Gesprächspartner.
 *
 * Dieselben zwei Pflichtfelder wie bei der roten Freigabe, aus demselben
 * Grund: Eine IBAN-Bestätigung ohne Rückruf ist keine Bestätigung, sondern
 * ein Klick (§15.1 S3).
 */
function IbanDialog({
  lieferant,
  laeuft,
  onAbbrechen,
  onBestaetigen,
}: {
  lieferant: SupplierResponse | null;
  laeuft: boolean;
  onAbbrechen: () => void;
  onBestaetigen: (daten: IbanBestaetigung) => void;
}) {
  const [iban, setIban] = useState('');
  const [nummer, setNummer] = useState('');
  const [gesprochenMit, setGesprochenMit] = useState('');
  const [kommentar, setKommentar] = useState('');

  const sauber = iban.replace(/\s+/g, '').toUpperCase();
  const bereit =
    sauber.length >= 15 && nummer.trim().length >= 5 && gesprochenMit.trim().length >= 2;

  const offene = (lieferant?.ibans ?? []).filter((i) => !i.confirmed_at);

  return (
    <Dialog
      offen={lieferant !== null}
      onSchliessen={onAbbrechen}
      titel="IBAN bestätigen"
      hinweis="Erst anrufen, dann bestätigen. Danach darf auf diese IBAN gezahlt werden."
      abbrechenBeiHintergrundklick={false}
      fussleiste={
        <>
          <Button variant="outline" onClick={onAbbrechen} disabled={laeuft}>
            Abbrechen
          </Button>
          <Button
            onClick={() =>
              onBestaetigen({
                iban: sauber,
                rueckruf_nummer: nummer.trim(),
                gesprochen_mit: gesprochenMit.trim(),
                kommentar: kommentar.trim() || null,
              })
            }
            disabled={!bereit}
            loading={laeuft}
          >
            Bestätigen
          </Button>
        </>
      }
    >
      <p className="mb-4 text-sm text-slate-600">
        <span className="font-medium text-slate-900">{lieferant?.name}</span> — ruft unter der
        Nummer an, die ihr schon habt: aus dem Vertrag, aus einer alten Rechnung, aus dem
        Impressum. Nicht unter der aus der Mail.
      </p>

      {offene.length > 0 && (
        <div className="mb-4 rounded-lg bg-warning-50 px-3 py-2 text-xs text-warning-700">
          Wartet noch auf Bestätigung:{' '}
          {offene.map((i) => ibanFormat(i.iban)).join(' · ')}
          <button
            type="button"
            onClick={() => setIban(offene[0].iban)}
            className="ml-2 underline underline-offset-2"
          >
            übernehmen
          </button>
        </div>
      )}

      <div className="space-y-3">
        <Feld label="IBAN" pflicht>
          <Eingabe
            value={iban}
            onChange={(e) => setIban(e.target.value)}
            placeholder="DE89 3704 0044 0532 0130 00"
            className="font-mono"
          />
        </Feld>
        <div className="grid gap-3 sm:grid-cols-2">
          <Feld label="Rückrufnummer" pflicht hinweis="Die Nummer, unter der ihr angerufen habt.">
            <Eingabe
              value={nummer}
              onChange={(e) => setNummer(e.target.value)}
              placeholder="0351 1234567"
              inputMode="tel"
            />
          </Feld>
          <Feld label="Gesprochen mit" pflicht hinweis="Wer war am Telefon?">
            <Eingabe
              value={gesprochenMit}
              onChange={(e) => setGesprochenMit(e.target.value)}
              placeholder="Frau Krause, Buchhaltung"
            />
          </Feld>
        </div>
        <Feld label="Anmerkung (freiwillig)" hinweis="Steht im Log.">
          <Textfeld
            rows={2}
            value={kommentar}
            onChange={(e) => setKommentar(e.target.value)}
            placeholder="Bankwechsel bestätigt, Schreiben liegt vor"
          />
        </Feld>
      </div>
    </Dialog>
  );
}

/**
 * Startbestand als JSON hochladen.
 *
 * Erwartet entweder `{"suppliers": [...]}` oder gleich die nackte Liste — wer
 * eine Datei aus einem anderen System exportiert, hat meist die Liste und
 * nicht den Umschlag. Beides anzunehmen kostet drei Zeilen und spart eine
 * Rückfrage.
 */
function SeedDialog({
  offen,
  laeuft,
  onAbbrechen,
  onEinlesen,
}: {
  offen: boolean;
  laeuft: boolean;
  onAbbrechen: () => void;
  onEinlesen: (daten: SeedImport) => void;
}) {
  const [text, setText] = useState('');
  const [ueberschreiben, setUeberschreiben] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const lesen = (datei: File) => {
    const leser = new FileReader();
    leser.onload = () => {
      setText(String(leser.result ?? ''));
      setFehler(null);
    };
    leser.readAsText(datei);
  };

  const senden = () => {
    let roh: unknown;
    try {
      roh = JSON.parse(text);
    } catch {
      setFehler('Das ist kein gültiges JSON.');
      return;
    }
    const suppliers = Array.isArray(roh)
      ? roh
      : (roh as { suppliers?: unknown }).suppliers;
    if (!Array.isArray(suppliers)) {
      setFehler('Erwartet wird eine Liste oder ein Objekt mit dem Schlüssel „suppliers“.');
      return;
    }
    const fehlender = suppliers.find(
      (e) => typeof (e as SupplierCreate).name !== 'string' || !(e as SupplierCreate).name,
    );
    if (fehlender) {
      setFehler('Jeder Eintrag braucht mindestens einen „name“.');
      return;
    }
    setFehler(null);
    onEinlesen({
      suppliers: (suppliers as SupplierCreate[]).map((e) => ({
        name: e.name,
        aliases: e.aliases ?? [],
        mail_domains: e.mail_domains ?? [],
        expected_interval: e.expected_interval ?? null,
        customer_number: e.customer_number ?? null,
        notes: e.notes ?? null,
      })),
      ueberschreiben,
    });
  };

  return (
    <Dialog
      offen={offen}
      onSchliessen={onAbbrechen}
      titel="Startbestand einlesen"
      hinweis="Eine JSON-Datei mit Lieferanten. IBANs kommen dabei nicht mit — die entstehen nur über einen Rückruf."
      breite="lg"
      abbrechenBeiHintergrundklick={false}
      fussleiste={
        <>
          <Button variant="outline" onClick={onAbbrechen} disabled={laeuft}>
            Abbrechen
          </Button>
          <Button onClick={senden} disabled={text.trim().length === 0} loading={laeuft}>
            Einlesen
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Feld label="Datei" hinweis="Oder unten einfügen.">
          <input
            type="file"
            accept="application/json,.json"
            onChange={(e) => {
              const datei = e.target.files?.[0];
              if (datei) lesen(datei);
            }}
            className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:text-slate-700 hover:file:bg-slate-200"
          />
        </Feld>

        <Feld label="JSON" hinweis='{"suppliers": [{"name": "…", "mail_domains": ["…"]}]}'>
          <Textfeld
            rows={8}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setFehler(null);
            }}
            className="font-mono text-xs"
          />
        </Feld>

        <label className="flex items-start gap-2.5 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={ueberschreiben}
            onChange={(e) => setUeberschreiben(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
          />
          <span>
            Bestehende Liste ersetzen
            <span className="mt-0.5 block text-xs text-slate-500">
              Ohne Haken werden vorhandene Lieferanten anhand des Namens ergänzt, nicht gelöscht.
            </span>
          </span>
        </label>

        {fehler && <p className="text-xs text-danger-600">{fehler}</p>}
      </div>
    </Dialog>
  );
}
